import type { ReactNode } from 'react';
import { IconTile, cn } from '@orgus/ui';
import { CircleAlert, CircleCheck, TriangleAlert } from '@orgus/ui/icons';

export type VerdictTone = 'ok' | 'attention' | 'danger' | 'neutral';

export interface Verdict {
  tone: VerdictTone;
  /** The one-glance answer, a few words: "Everything is safe", "2 alerts need you". */
  title: string;
  /** One short dim line: why, or what is next. */
  line?: string;
}

const TILE = { ok: 'success', attention: 'attention', danger: 'danger', neutral: 'neutral' } as const;
const ICON = { ok: <CircleCheck />, attention: <TriangleAlert />, danger: <CircleAlert />, neutral: <CircleCheck /> };
const LABEL = { ok: 'All clear', attention: 'Needs you', danger: 'Act now', neutral: 'Status' };

/**
 * A page's one-glance answer at its top: is everything OK, and what needs me. One tinted icon,
 * one bright sentence, one dim line, at most one action. Calm when all is well; colour only
 * when a person must act. Beside it, an optional figure (a ring or a shape) that sums the page.
 */
export function PageVerdict({ v, action, figure, className }: { v: Verdict; action?: ReactNode; figure?: ReactNode; className?: string }) {
  return (
    <section
      aria-label="At a glance"
      role="status"
      data-verdict={v.tone}
      className={cn('flex min-w-0 flex-wrap items-center gap-4 rounded-card border border-border bg-surface px-4 py-4 shadow-elevation-flat sm:px-6', className)}
    >
      <IconTile icon={ICON[v.tone]} tone={TILE[v.tone]} />
      <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1">
        <span className="sr-only">{LABEL[v.tone]}: </span>
        <p className="text-title font-medium text-fg">{v.title}</p>
        {v.line && <p className="line-clamp-2 text-caption text-fg-subtle">{v.line}</p>}
      </div>
      {figure != null && <div className="flex shrink-0 items-center max-sm:hidden">{figure}</div>}
      {action != null && <div className="flex shrink-0 items-center max-sm:w-full max-sm:pl-12">{action}</div>}
    </section>
  );
}
